import { Controller } from "@hotwired/stimulus"

// Manages a dynamic tag list with a custom autocomplete dropdown.
//
// data-tag-input-name-value         — hidden input name, e.g. "previews[0][tags]"
// data-tag-input-suggestions-value  — JSON array of {value, label} objects
// data-tag-input-initial-tags-value — JSON array of pre-filled tag strings
export default class extends Controller {
  static targets = ["input", "pills", "hidden", "dropdown"]
  static values  = {
    name:         String,
    suggestions:  Array,
    initialTags:  Array,
  }

  connect() {
    this._tags = [...this.initialTagsValue]
    this._render()
    this._boundDocClick = this._onDocumentClick.bind(this)
    document.addEventListener("click", this._boundDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this._boundDocClick)
  }

  // Text input: show all suggestions on focus/empty, filter as user types
  filter() {
    const query = this.inputTarget.value.trim()
    const lower = query.toLowerCase()
    const matches = (query
      ? this.suggestionsValue.filter(s =>
          s.value.includes(lower) || s.label.toLowerCase().includes(lower)
        )
      : this.suggestionsValue
    ).slice().sort((a, b) => a.value.localeCompare(b.value))
    this._showDropdown(matches, query)
  }

  // "Add" button or Enter with no highlighted item
  add(event) {
    event.preventDefault()
    const raw = this.inputTarget.value.trim()
    if (!raw) return
    this._commitTag(this._normalise(raw))
  }

  // Click on a dropdown suggestion row
  pick(event) {
    event.preventDefault()
    this._commitTag(event.currentTarget.dataset.tagValue)
  }

  // × button on a pill
  remove(event) {
    event.preventDefault()
    const tag = event.currentTarget.dataset.tagValue
    this._tags = this._tags.filter(t => t !== tag)
    this._render()
  }

  keydown(event) {
    switch (event.key) {
      case "Enter": {
        event.preventDefault()
        const highlighted = this.dropdownTarget.querySelector("[data-highlighted]")
        if (highlighted) {
          this._commitTag(highlighted.dataset.tagValue)
        } else {
          this.add(event)
        }
        break
      }
      case "ArrowDown":
        event.preventDefault()
        this._moveHighlight(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this._moveHighlight(-1)
        break
      case "Escape":
        this._hideDropdown()
        break
    }
  }

  // -- Private -----------------------------------------------------------------

  _commitTag(tag) {
    if (tag && !this._tags.includes(tag)) {
      this._tags = [...this._tags, tag]
      this._render()
    }
    this.inputTarget.value = ""
    this._hideDropdown()
    this.inputTarget.blur()
  }

  _showDropdown(matches, query) {
    let items = matches.map(s => this._dropdownItem(s.value, s.value))
    if (items.length === 0 && query) {
      items = [this._dropdownItem(this._normalise(query), `Create "${query}"`)]
    }
    this.dropdownTarget.innerHTML = items.join("")
    this.dropdownTarget.classList.remove("d-none")
  }

  _hideDropdown() {
    this.dropdownTarget.innerHTML = ""
    this.dropdownTarget.classList.add("d-none")
  }

  _dropdownItem(value, label) {
    const v = this._escapeHtml(value)
    const l = this._escapeHtml(label)
    return `<button type="button" class="list-group-item list-group-item-action py-1 px-2"
                     data-tag-value="${v}" data-action="click->tag-input#pick mousedown->tag-input#pick">${l}</button>`
  }

  _moveHighlight(direction) {
    const items = [...this.dropdownTarget.querySelectorAll("[data-tag-value]")]
    if (!items.length) return
    const current = this.dropdownTarget.querySelector("[data-highlighted]")
    const idx = current ? items.indexOf(current) : -1
    if (current) { delete current.dataset.highlighted; current.classList.remove("active") }
    let next = idx + direction
    if (next < 0) next = items.length - 1
    if (next >= items.length) next = 0
    items[next].dataset.highlighted = "1"
    items[next].classList.add("active")
  }

  _onDocumentClick(event) {
    if (!this.element.contains(event.target)) this._hideDropdown()
  }

  _normalise(raw) {
    const lower = raw.toLowerCase().trim()
    const byLabel = this.suggestionsValue.find(s => s.label.toLowerCase() === lower)
    if (byLabel) return byLabel.value
    const byValue = this.suggestionsValue.find(s => s.value === lower)
    if (byValue) return byValue.value
    return lower.replace(/\s+/g, "-")
  }

  _labelFor(tag) {
    return tag
  }

  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }

  _render() {
    this.pillsTarget.innerHTML = this._tags.map(tag => `
      <span class="d-inline-flex align-items-center rounded-pill text-white" style="background:#6c757d; font-size:0.8rem; padding:0.1em 0.55em; margin:0 0.3em 0.3em 0; gap:0.35em;">
        ${this._escapeHtml(this._labelFor(tag))}
        <button type="button"
                style="background:none; border:none; color:white; font-size:0.9rem; line-height:1; padding:0; cursor:pointer; opacity:0.8;"
                aria-label="Remove"
                data-tag-value="${this._escapeHtml(tag)}"
                data-action="click->tag-input#remove">&#x2715;</button>
      </span>`).join("")

    const name = this.nameValue
    this.hiddenTarget.innerHTML = this._tags.map(tag =>
      `<input type="hidden" name="${this._escapeHtml(name)}[]" value="${this._escapeHtml(tag)}">`
    ).join("")
  }
}
